import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useAction } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import { isValidProjectId } from '../../utils/projectIds';
import useFileStore from '../../stores/fileStore';
import { getErrorMessage } from '../../utils/errors';
import CanvasEditor from '../Editor/CanvasEditor';
import './CanvasStudioPage.css';

const DEFAULT_CANVAS_PATH = 'Notes/project-canvas.canvas';
const DEFAULT_CANVAS_NAME = 'project-canvas.canvas';

function CanvasStudioPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const safeProjectId = isValidProjectId(projectId) ? projectId : null;

  const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const files = useQuery(api.files.listByProject, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const storyBibleEntries = useQuery(api.storyBibleEntries.list, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const storyBible = useQuery(api.storyBibles.getCurrent, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const createItem = useMutation(api.files.createItem);
  const syncCanvasFromEntries = useAction(api.storyBibleEntries.syncCanvasFromEntries);
  const syncFromCanvas = useAction(api.storyBibleEntries.syncFromCanvas);
  const syncCanvasLegacy = useAction(api.ai.syncCanvasFromStoryBible);
  const { loadFiles, getFileContent } = useFileStore();

  const [ready, setReady] = useState(false);
  const [canvasPath, setCanvasPath] = useState(DEFAULT_CANVAS_PATH);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [syncError, setSyncError] = useState('');

  const handleSyncFromStoryBible = async () => {
    if (!safeProjectId || isSyncing) return;
    setIsSyncing(true);
    setSyncMessage('');
    setSyncError('');
    try {
      // Use new entries-based sync if entries exist, otherwise fall back to legacy
      const hasEntries = storyBibleEntries && storyBibleEntries.length > 0;
      let result;
      if (hasEntries) {
        result = await syncCanvasFromEntries({ projectId: safeProjectId, canvasPath });
      } else {
        result = await syncCanvasLegacy({ projectId: safeProjectId, canvasPath });
      }
      setSyncMessage(t('canvasStudio.syncedFromStoryBible', { nodes: result.nodeCount, edges: result.edgeCount }));
      await loadFiles(safeProjectId);
      setReady(false);
      setTimeout(() => setReady(true), 100);
    } catch (err) {
      setSyncError(getErrorMessage(err, t('canvasStudio.syncFailed')));
    } finally {
      setIsSyncing(false);
    }
  };

  const handlePushToStoryBible = async () => {
    if (!safeProjectId || isSyncing) return;
    setIsSyncing(true);
    setSyncMessage('');
    setSyncError('');
    try {
      const content = getFileContent(canvasPath);
      if (!content) throw new Error(t('canvasStudio.noCanvasData'));
      const result = await syncFromCanvas({ projectId: safeProjectId, canvasJson: content });
      const parts = [];
      if (result.newEntries > 0) parts.push(t('canvasStudio.newEntries', { count: result.newEntries }));
      if (result.newConnections > 0) parts.push(t('canvasStudio.newConnections', { count: result.newConnections }));
      setSyncMessage(parts.length > 0 ? t('canvasStudio.pushedToStoryBible', { changes: parts.join(', ') }) : t('canvasStudio.alreadyInSync'));
    } catch (err) {
      setSyncError(getErrorMessage(err, t('canvasStudio.pushFailed')));
    } finally {
      setIsSyncing(false);
    }
  };

  // Find or create the default canvas file
  useEffect(() => {
    if (!files || !safeProjectId) return;

    const canvasFile = files.find((f) => f.path.endsWith('.canvas'));
    if (canvasFile) {
      setCanvasPath(canvasFile.path);
      setReady(true);
      return;
    }

    // Auto-create the default canvas
    (async () => {
      try {
        await createItem({
          projectId: safeProjectId,
          path: DEFAULT_CANVAS_PATH,
          name: DEFAULT_CANVAS_NAME,
          parentPath: 'Notes',
          type: 'file',
          content: JSON.stringify({ nodes: [], edges: [] }),
        });
        await loadFiles(safeProjectId);
        setCanvasPath(DEFAULT_CANVAS_PATH);
        setReady(true);
      } catch (err) {
        // If creation fails (e.g. already exists), try to proceed
        setReady(true);
      }
    })();
  }, [files, safeProjectId, createItem, loadFiles]);

  if (!safeProjectId) {
    return (
      <main className="canvas-studio loading">
        <p>{t('common.invalidProjectLink')}</p>
        <button className="btn btn-secondary" onClick={() => navigate('/projects')}>{t('common.back')}</button>
      </main>
    );
  }

  if (project === undefined || files === undefined) {
    return <main className="canvas-studio loading"><p>{t('canvasStudio.loading')}</p></main>;
  }

  if (!project) {
    return <main className="canvas-studio loading"><p>{t('common.projectNotFound')}</p></main>;
  }

  return (
    <main className="canvas-studio">
      <header className="canvas-studio-header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/projects/${safeProjectId}`)}>
          {t('common.back')}
        </button>
        <h1>{t('projects.stages.canvasStudio')}</h1>
        <span className="canvas-studio-project">{project.title}</span>
        <button
          className="btn btn-secondary btn-sm"
          onClick={handlePushToStoryBible}
          disabled={isSyncing}
          title={t('canvasStudio.pushBackTitle')}
        >
          {isSyncing ? t('canvasStudio.pushing') : t('canvasStudio.pushToStoryBible')}
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleSyncFromStoryBible}
          disabled={isSyncing || (!storyBible && (!storyBibleEntries || storyBibleEntries.length === 0))}
          title={t('canvasStudio.populateTitle')}
        >
          {isSyncing ? t('canvasStudio.syncing') : t('canvasStudio.syncFromStoryBible')}
        </button>
      </header>
      {syncMessage && <div className="canvas-sync-message canvas-sync-success">{syncMessage}</div>}
      {syncError && <div className="canvas-sync-message canvas-sync-error">{syncError}</div>}

      <div className="canvas-studio-editor">
        {ready ? (
          <CanvasEditor projectId={safeProjectId} path={canvasPath} />
        ) : (
          <p className="canvas-studio-loading">{t('canvasStudio.settingUp')}</p>
        )}
      </div>
    </main>
  );
}

export default CanvasStudioPage;
